import { TiktokLinkStateCue } from "@/components/tiktok-link-state-cue"
import { TiktokVerifiedBadge } from "@/components/tiktok-verified-badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import type { MembershipIdentity } from "@/types/creator-analytics"

import { CreatorAnalyticsTabs } from "./creator-analytics-tabs"

export interface CreatorAnalyticsIdentityHeaderProps {
  membership: MembershipIdentity
}

/**
 * OKS-64 — Compact identity header for the per-creator analytics
 * page. Renders avatar + display name + @handle + the OAuth-link
 * cue (`TiktokLinkStateCue`) on the right, with the 2-tab strip
 * below (Overview · Analytics).
 *
 * Used ONLY by `Brand/Crew/Analytics.tsx`. The existing
 * `Brand/Crew/Show.tsx` keeps its richer header (breadcrumb +
 * lifecycle alerts + action bar) and renders the
 * `CreatorAnalyticsTabs` strip directly underneath.
 *
 * Per UX Spec §1: identity header is persistent across the tab
 * swap because both pages mount the same component family.
 */
export function CreatorAnalyticsIdentityHeader({
  membership,
}: CreatorAnalyticsIdentityHeaderProps) {
  const initials = deriveInitials(membership.display_name)

  return (
    <header
      data-testid="creator-analytics-identity-header"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center gap-4">
        <Avatar className="h-12 w-12">
          {membership.avatar_url && (
            <AvatarImage
              src={membership.avatar_url}
              alt={membership.display_name}
            />
          )}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div
            className="truncate text-lg font-semibold"
            data-testid="creator-analytics-display-name"
          >
            {membership.display_name}
          </div>
          {membership.handle && (
            <div className="flex items-center gap-2 text-sm">
              <span
                className="text-muted-foreground font-mono"
                data-testid="creator-analytics-handle"
              >
                @{membership.handle}
              </span>
              <TiktokVerifiedBadge verified={true} />
              <TiktokLinkStateCue state={membership.tiktok_link_state} />
            </div>
          )}
        </div>
      </div>
      <CreatorAnalyticsTabs
        active="analytics"
        overviewPath={membership.overview_path}
        analyticsPath={membership.analytics_path}
      />
    </header>
  )
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2)
}

export default CreatorAnalyticsIdentityHeader
